import {
  createContext,
  createElement,
  Fragment,
  type ReactElement,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
} from 'react';

import type {DualModeReactRenderer} from './DualModeReactRenderer';
import {RampIdType} from './generated/graphql';
import {useUserRampFlag} from './util/useRampFlag';

const RenderModeContext = createContext<DualModeReactRenderer | null>(null);

export const RenderModeContextProvider = RenderModeContext.Provider;

function useSetRenderMode() {
  const renderEngine = useContext(RenderModeContext);
  if (!renderEngine) {
    throw new Error(
      'useRenderEngine must be used within a DualModeReactRenderer'
    );
  }

  return useCallback(
    (renderMode: 'concurrent' | 'legacy') => {
      renderEngine.changeRenderMode(renderMode);
    },
    [renderEngine]
  );
}

type AutoRenderModeSwitcherProps = {children: ReactNode};

/**
 * This component automatically switches between concurrent and legacy render
 * modes based on user feature flag.
 *
 * @param children
 */
export function ConcurrentRenderModeSwitcher({
  children,
}: AutoRenderModeSwitcherProps): ReactElement {
  const setRenderMode = useSetRenderMode();
  const isConcurrentFeatureActive = useUserRampFlag(
    'react_concurrent_mode',
    RampIdType.UserName
  );

  useEffect(() => {
    if (isConcurrentFeatureActive) {
      setRenderMode('concurrent');
    }
  }, [isConcurrentFeatureActive, setRenderMode]);

  return createElement(Fragment, {}, children);
}
